import { useState } from 'react';

import { Button } from '@nesso/ui/primitives/button';
import { Skeleton } from '@nesso/ui/primitives/skeleton';

import type { SuperbillLocation } from '@/entities/client-billing-settings';
import type { PracticeLocation } from '@/entities/practice';
import { SettingsRow } from '@/shared/ui/settings/settings-row';

import { formatSuperbillLocationLabel } from '../../../lib/format-superbill-location-label';
import { getDisplayAddress } from '../../../model/get-display-address';
import { AddLocationSheet } from './display-address-row/add-location-sheet';
import { DisplayAddressEditor } from './display-address-row/display-address-editor';

const LABEL = 'Display address';

type Props = {
    locations: PracticeLocation[] | undefined;
    storedLocation: SuperbillLocation | undefined;
    error: boolean;
};

export function DisplayAddressRow({ locations, storedLocation, error }: Props) {
    const [editing, setEditing] = useState(false);
    const [adding, setAdding] = useState(false);
    const [saving, setSaving] = useState(false);

    if (error) {
        return <SettingsRow label={LABEL} caption="Unable to load the display address." />;
    }

    if (!locations || !storedLocation) {
        return <SettingsRow label={LABEL} caption={<Skeleton className="h-5 w-56" />} />;
    }

    const displayAddress = getDisplayAddress(locations, storedLocation);

    if (displayAddress.kind === 'ADD_LOCATION') {
        return (
            <>
                <SettingsRow
                    label={LABEL}
                    caption={
                        <Button
                            type="button"
                            variant="link"
                            className="h-auto p-0 text-sm font-normal text-muted-foreground underline"
                            onClick={() => setAdding(true)}
                        >
                            Add a location
                        </Button>
                    }
                    action={{ label: 'Add', onClick: () => setAdding(true) }}
                />
                <AddLocationSheet open={adding} onOpenChange={setAdding} />
            </>
        );
    }

    if (editing) {
        return (
            <SettingsRow
                label={LABEL}
                action={{ label: 'Cancel', onClick: () => setEditing(false), disabled: saving }}
            >
                <DisplayAddressEditor
                    storedLocation={displayAddress.kind}
                    defaultLocationName={displayAddress.defaultLocationName}
                    onSaved={() => setEditing(false)}
                    onPendingChange={setSaving}
                />
            </SettingsRow>
        );
    }

    return (
        <SettingsRow
            label={LABEL}
            caption={formatSuperbillLocationLabel(
                displayAddress.kind,
                displayAddress.defaultLocationName,
            )}
            action={{
                label: 'Edit',
                onClick: () => setEditing(true),
                ariaLabel: 'Edit display address',
            }}
        />
    );
}
